import React, { useEffect, useId, useRef, useState } from "react";
import { toYesNoDisplay, serializeEditablePayload } from "../../utilities/commercialChecklist";

import CharacterCount from "../ui/CharacterCount";

// Shared building blocks for the commercial tables (Checklist + Task List).
// Both tables are column-config driven, so the editable cell, sortable header
// and filter dropdown are identical between them and live here to avoid
// duplication. (The auto-refresh control lives in ./commercialRefresh.)

// Text search — same grey field, icon and clear control as the trips page.
export const CommercialSearch = ({ value, onChange, placeholder, ariaLabel }) => (
    <div className="commercial__search-wrap">
        <input
            type="text"
            className="commercial__search"
            placeholder={placeholder}
            aria-label={ariaLabel}
            value={value}
            onChange={(e) => onChange(e.target.value)}
        />
        {value ? (
            <button
                type="button"
                className="commercial__search-clear"
                onClick={() => onChange("")}
                aria-label="Clear search"
            />
        ) : null}
    </div>
);

// Multi-select filter dropdown (closes on outside click).
export const FilterDropdown = ({ label, icon, options, selected, onToggle, onClear }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const buttonRef = useRef(null);
    const menuId = useId();

    useEffect(() => {
        const onDown = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
        document.addEventListener("pointerdown", onDown);
        return () => document.removeEventListener("pointerdown", onDown);
    }, []);

    return (
        <div
            className="commercial__filter"
            ref={ref}
            onKeyDown={(event) => {
                if (event.key === "Escape") {
                    setOpen(false);
                    buttonRef.current?.focus();
                }
            }}
        >
            <div className="portal--filter_toggle">
                <button
                    type="button"
                    ref={buttonRef}
                    aria-expanded={open}
                    aria-controls={menuId}
                    className={selected.length ? "is-active" : ""}
                    onClick={() => setOpen((o) => !o)}
                >
                    {icon && <img src={icon} alt="" className="commercial__filter-icon" />}
                    <span>
                        {label}
                        {selected.length ? ` (${selected.length})` : ""}
                    </span>
                </button>
            </div>

            {open && (
                <div className="commercial__filter-menu" id={menuId} role="group" aria-label={`${label} options`}>
                    {options.length === 0 && <div className="commercial__filter-empty">No options</div>}
                    {options.map((opt) => (
                        <label key={opt} className="commercial__filter-option">
                            <input type="checkbox" checked={selected.includes(opt)} onChange={() => onToggle(opt)} />
                            <span>{opt}</span>
                        </label>
                    ))}
                    {selected.length > 0 && (
                        <button type="button" className="commercial__filter-clear" onClick={onClear}>
                            Clear
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

// Inline editable cell — auto-saves on blur (or on change for selects). Fields
// flagged `stub` update locally only until their backend field exists.
// `row` is the record being edited (a deal on the Checklist, a task on the Task
// List); `onSaveField(row, payloadKey, value, dealKey, localValue)` persists it.
export const EditableCell = ({ row, config, onSaveField }) => {
    const countId = useId();
    const seed = () => {
        const v = row[config.dealKey];
        if (v == null) return "";
        if (config.valueType === "yesNo") return toYesNoDisplay(v) ?? "";
        return String(v);
    };

    const [value, setValue] = useState(seed);
    const [status, setStatus] = useState("idle");
    const savedRef = useRef(seed());
    const textareaRef = useRef(null);

    const autoSize = () => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight}px`;
    };

    useEffect(() => {
        if (config.type === "textarea") autoSize();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, config.type]);

    useEffect(() => {
        const next = seed();
        setValue(next);
        if (next !== savedRef.current) {
            savedRef.current = next;
            setStatus("idle");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [row, config.dealKey]);

    const commit = async (raw) => {
        const next = (raw ?? value).trim();
        if (next === savedRef.current) return;

        if (config.stub) {
            savedRef.current = next;
            setStatus("saved");
            return;
        }

        setStatus("saving");
        const payloadValue = serializeEditablePayload(config, next);
        const ok = await onSaveField(row, config.payloadKey, payloadValue, config.dealKey, next);
        savedRef.current = ok ? next : savedRef.current;
        setStatus(ok ? "saved" : "error");
    };

    const statusLabel = { saving: "Saving…", saved: "Saved", error: "Not saved" }[status];

    const selectOptions =
        config.type === "select"
            ? [...new Set([...(config.options || []), ...(value && !config.options?.includes(value) ? [value] : [])])]
            : [];

    return (
        <div className="commercial__cell-edit">
            {config.type === "select" ? (
                <select
                    className="commercial__cell-input"
                    value={value}
                    onChange={(e) => {
                        setValue(e.target.value);
                        commit(e.target.value);
                    }}
                >
                    <option value="" disabled={config.required}>
                        –
                    </option>
                    {selectOptions.map((opt) => (
                        <option key={opt} value={opt}>
                            {opt}
                        </option>
                    ))}
                </select>
            ) : config.type === "textarea" ? (
                <textarea
                    ref={textareaRef}
                    maxLength={config.maxLength}
                    aria-describedby={config.maxLength ? countId : undefined}
                    className="commercial__cell-input"
                    rows={2}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onPaste={() => requestAnimationFrame(autoSize)}
                    onBlur={(e) => {
                        setValue(e.target.value);
                        commit(e.target.value);
                    }}
                />
            ) : (
                <input
                    type="text"
                    className="commercial__cell-input"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onBlur={(e) => {
                        setValue(e.target.value);
                        commit(e.target.value);
                    }}
                />
            )}
            {config.maxLength && <CharacterCount id={countId} count={value.length} maxLength={config.maxLength} />}
            {statusLabel && (
                <span className={`commercial__cell-status commercial__cell-status--${status}`}>{statusLabel}</span>
            )}
        </div>
    );
};

export const SortableHeaderLabel = ({ col, sort, onSort }) => {
    const active = sort.key === col.key;
    const indicator = active ? (sort.dir === "asc" ? "▲" : "▼") : "↕";
    return (
        <button
            type="button"
            className={`commercial__sort${active ? " is-active" : ""}`}
            onClick={() => onSort(col.key)}
            aria-label={`Sort by ${col.label}`}
        >
            <span>{col.label}</span>
            <span className={`commercial__sort-indicator${active ? "" : " commercial__sort-indicator--inactive"}`}>
                {indicator}
            </span>
        </button>
    );
};
